import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, CheckCircle2 } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

export default function EntregaDetalle() {
    const { assignmentId, submissionId } = useParams();
    const navigate = useNavigate();
    const [assignment, setAssignment] = useState(null);
    const [submission, setSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [score, setScore] = useState("");
    const [feedback, setFeedback] = useState("");
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [ar, sr] = await Promise.all([
                api.get(`/assignments/${assignmentId}`),
                api.get(`/assignments/${assignmentId}/submissions/${submissionId}`),
            ]);
            setAssignment(ar.data);
            setSubmission(sr.data);
            setScore(sr.data.score ?? "");
            setFeedback(sr.data.feedback || "");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando la entrega");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [assignmentId, submissionId]);

    async function saveGrade() {
        const numScore = parseInt(score, 10);
        if (isNaN(numScore) || numScore < 0) { toast.error("Ingresá una nota válida"); return; }
        if (numScore > assignment.max_score) { toast.error(`La nota no puede ser mayor a ${assignment.max_score}`); return; }
        setSaving(true);
        try {
            await api.patch(`/assignments/${assignmentId}/submissions/${submissionId}`, { score: numScore, feedback: feedback.trim() || null });
            toast.success("Calificación guardada");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando calificación");
        } finally {
            setSaving(false);
        }
    }

    if (loading) return <div className="p-10 text-edu-muted">Cargando…</div>;
    if (!assignment || !submission) return <div className="p-10 text-edu-muted">Entrega no encontrada.</div>;

    const isGraded = submission.score !== null && submission.score !== undefined;
    const report = submission.ai_grammar_report;

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-4xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver a las entregas
            </button>

            <div className="edu-card p-6 mb-6">
                <p className="text-xs text-edu-muted uppercase tracking-widest font-semibold">{assignment.title}</p>
                <h1 className="text-2xl font-semibold mt-1">{submission.student_name}</h1>
                <p className="text-sm text-edu-muted mt-1">Entregado el {new Date(submission.submitted_at).toLocaleString("es-PE")}</p>
            </div>

            {/* Trabajo enviado */}
            <div className="edu-card p-6 mb-6">
                <h2 className="text-sm font-semibold text-edu-navy mb-3">Trabajo enviado</h2>
                {submission.delivery_type === "text" ? (
                    <p className="text-sm text-edu-body whitespace-pre-wrap bg-edu-soft rounded-xl p-4">{submission.text}</p>
                ) : submission.delivery_type === "pdf" ? (
                    <div>
                        <a href={fileUrl(submission.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-edu hover:bg-edu-soft mb-3">
                            <Eye size={13} /> Abrir PDF en otra pestaña
                        </a>
                        <iframe title="Entrega del alumno" src={fileUrl(submission.file_id)} className="w-full h-[600px] rounded-xl border border-edu" />
                    </div>
                ) : (
                    <a href={fileUrl(submission.file_id)} target="_blank" rel="noreferrer">
                        <img src={fileUrl(submission.file_id)} alt="Entrega del alumno" className="max-w-full rounded-xl border border-edu" />
                    </a>
                )}
            </div>

            {/* Corrección automática de gramática */}
            {report && (
                <div className="edu-card p-6 mb-6 border-l-4 border-l-violet-400">
                    <h2 className="text-sm font-semibold text-violet-800 mb-1">
                        Corrección automática de gramática (IA) — {report.error_count} error{report.error_count !== 1 ? "es" : ""}
                    </h2>
                    <p className="text-xs text-violet-700 mb-3">{report.summary}</p>
                    {report.has_errors ? (
                        <div className="space-y-2">
                            {(report.errors || []).map((err, i) => (
                                <div key={i} className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                                    <p className="text-sm text-violet-900 font-medium">"{err.excerpt}"</p>
                                    <p className="text-xs text-violet-500 uppercase tracking-wide mt-0.5">{err.type}</p>
                                    <p className="text-xs text-violet-800 mt-1"><span className="font-medium">Qué está mal:</span> {err.issue}</p>
                                    <p className="text-xs text-violet-700 mt-0.5"><span className="font-medium">Explicación:</span> {err.explanation}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-violet-700">Sin errores de gramática relevantes detectados.</p>
                    )}
                </div>
            )}

            {/* Comentarios según la rúbrica que subiste */}
            {submission.ai_rubric_report && (submission.ai_rubric_report.criterios || []).length > 0 && (
                <div className="edu-card p-6 mb-6 border-l-4 border-l-blue-400">
                    <h2 className="text-sm font-semibold text-blue-800 mb-1">Comentarios según tu rúbrica (IA)</h2>
                    {submission.ai_rubric_report.observaciones_generales && (
                        <p className="text-xs text-blue-700 mb-3">{submission.ai_rubric_report.observaciones_generales}</p>
                    )}
                    <div className="space-y-2">
                        {submission.ai_rubric_report.criterios.map((c, i) => {
                            const badge = c.cumple === "cumple"
                                ? { label: "Cumple", cls: "bg-emerald-100 text-emerald-700" }
                                : c.cumple === "parcial"
                                    ? { label: "Parcial", cls: "bg-amber-100 text-amber-700" }
                                    : { label: "No cumple", cls: "bg-red-100 text-red-700" };
                            return (
                                <div key={i} className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                        <p className="text-sm text-blue-900 font-medium">{c.criterio}</p>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                                    </div>
                                    <p className="text-xs text-blue-700">{c.comentario}</p>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-edu-muted mt-3">Esto es apoyo de la IA, no una nota. La calificación final la pones tú abajo.</p>
                </div>
            )}

            {/* Calificación */}
            <div className="edu-card p-6">
                <h2 className="text-sm font-semibold text-edu-navy mb-3 inline-flex items-center gap-1.5">
                    {isGraded && <CheckCircle2 size={14} className="text-emerald-600" />} Calificación
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div>
                        <span className="text-[10px] text-edu-muted font-semibold uppercase tracking-widest block mb-1">Nota (máx. {assignment.max_score})</span>
                        <input type="number" min={0} max={assignment.max_score} value={score} onChange={(e) => setScore(e.target.value)} className="edu-input w-full text-sm py-2" placeholder="—" />
                    </div>
                    <div className="md:col-span-2">
                        <span className="text-[10px] text-edu-muted font-semibold uppercase tracking-widest block mb-1">Comentario para el alumno (opcional)</span>
                        <input value={feedback} onChange={(e) => setFeedback(e.target.value)} className="edu-input w-full text-sm py-2" placeholder="Retroalimentación para el alumno" />
                    </div>
                </div>
                <div className="flex justify-end mt-4">
                    <button onClick={saveGrade} disabled={saving || score === ""} className="edu-btn-primary px-4 py-2 text-sm disabled:opacity-50">
                        {saving ? "Guardando…" : isGraded ? "Actualizar calificación" : "Calificar"}
                    </button>
                </div>
            </div>
        </div>
    );
}
